/**
 * @author Maxime Louf
 * inspiré de https://www.w3.org/WAI/content-assets/wai-aria-practices/patterns/disclosure/examples/js/disclosure-button.js
 * sous licence W3C pour une partie du code https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 * @version 2.1.4
 *
 * @param trigger HTMLElement - L'élément HTML button cible
 * @option delay number - 'default' = 0 - Délai pour l'application du display: none; Utile pour les animations CSS (non compatible avec l'animation 'slide')
 * @option slide boolean - 'default' = false - Ajoute une animation 'slide'
 * @option slideSpeed number - 'default' = 0 - La vitesse de l'animation, requiert 'slide' à true
 * @option outsideClick boolean - 'default' = true - La vitesse de l'animation, requiert 'slide' à true
 * @option displayProperty string -'default' = 'block' - La valeur de display lorsque le panel est affiché
 * @option disableAutoFocus boolean - 'default' = false - Désactive le retour de focus lors de la fermeture à l'élément ouvrant
 * @option closeOnBlur boolean - 'default' = false - Lorsque le focus sort du panneau courant, il se ferme. Utile pour les panneaux de navigation
 * @data-option dataToggleCollapsed string - 'default' = null - Le texte du bouton quand le panel est caché
 * @data-option dataToggleExpanded string - 'default' = null - Le texte du bouton quand le panel est affiché
 * @export {Disclosure, DisclosureClose}
 */

"use strict";

// Facultatif - permet d'animer le disclosure
import {slideUp, slideDown} from './slideToggle';

export class Disclosure {
    constructor(trigger, options = {}) {
        // Nodes
        this.trigger = trigger;
        this.panel = '';
        this.id = this.trigger.getAttribute("aria-controls");

        // Options
        const {
            delay = 0,
            slide = true,
            slideSpeed = 300,
            outsideClick = true,
            displayProperty = 'block',
            disableAutoFocus = true,
            closeOnBlur = false,
        } = options;

        const dataSlide = this.trigger.getAttribute("data-slide");
        const dataSlideSpeed = this.trigger.getAttribute("data-slide-speed");
        const dataDelay = this.trigger.getAttribute("data-delay");
        const dataClickOutside = this.trigger.getAttribute("data-outside-click");
        const dataDisplayProperty = this.trigger.getAttribute("data-display-property");
        const dataDisableAutoFocus = this.trigger.getAttribute("data-disable-autofocus");
        const dataCloseOnBlur = this.trigger.getAttribute("data-close-on-blur");
        const dataToggleCollapsed = this.trigger.getAttribute("data-toggle-collapsed");
        const dataToggleExpanded = this.trigger.getAttribute("data-toggle-expanded");

        this.slide = (dataSlide ? (dataSlide === 'true') : null) ?? slide;
        this.slideSpeed = (dataSlideSpeed ? dataSlideSpeed : null) ?? slideSpeed;
        this.delay = (dataDelay ? (dataDelay === 'true') : null) ?? delay;
        this.outsideClick = (dataClickOutside ? (dataClickOutside === 'true') : null) ?? outsideClick;
        this.displayProperty = (dataDisplayProperty ? dataDisplayProperty : null) ?? displayProperty;
        this.disableAutoFocus = (dataDisableAutoFocus ? (dataDisableAutoFocus === 'true') : null) ?? disableAutoFocus;
        this.closeOnBlur = (dataCloseOnBlur ? (dataCloseOnBlur === 'true') : null) ?? closeOnBlur;
        this.dataToggleCollapsed = (dataToggleCollapsed ? dataToggleCollapsed : null);
        this.dataToggleExpanded = (dataToggleExpanded ? dataToggleExpanded : null);

        // On retrouve le panneau automatiquement avec l'aria-controls
        if (this.id) {
            this.panel = document.getElementById(this.id);
        }

        // S'il n'y a pas d'animation, la vitesse d'animation est 0
        if (this.slide === false) {
            this.slideSpeed = 0;
        }

        // Si l'attribut aria-expanded est manquant, on l'ajoute avec la valeur "false".
        if (!this.trigger.hasAttribute('aria-expanded')) {
            this.trigger.setAttribute("aria-expanded", "false");
        }

        this.isExpanded = this.trigger.getAttribute('aria-expanded');

        this.throwErrors();

        // On vérifie si l'attribut aria-expanded et à true et si l'élément à afficher n'est pas visible
        // Dans ce cas, on affiche l'élément automatiquement
        if (this.isOpen() && !this.isVisible()) {
            this.show()
        }
    }

    init() {
        // On applique les écouteurs d'événements
        this.trigger.addEventListener("click", this.onClick.bind(this));
        this.trigger.addEventListener("focus", this.onFocus.bind(this));
        this.trigger.addEventListener("blur", this.onBlur.bind(this));
        this.trigger.addEventListener("keydown", this.onKeydown.bind(this));
        this.panel.addEventListener("keydown", this.onKeydown.bind(this));
        if (this.closeOnBlur) {
            this.panel.addEventListener("keydown", this.onTabKeyDown.bind(this));
        }
    }

    removePanelEventListener() {
        this.panel.removeEventListener("keydown", this.onKeydown.bind(this));
        if (this.closeOnBlur) {
            this.panel.removeEventListener("keydown", this.onTabKeyDown.bind(this));
        }
    }

    /**
     * Etat courant du bouton
     */
    isOpen() {
        return (this.isExpanded === 'true');
    }

    /**
     * Etat courant de l'affichage de l'élément controllé
     */
    isVisible() {
        return (window.getComputedStyle(this.panel).display !== 'none');
    }

    /**
     * Affiche le contenu du nœud controllé
     */
    show() {
        const self = this;
        this.trigger.setAttribute("aria-expanded", "true");
        this.trigger.classList.add('showing');
        self.trigger.dispatchEvent(new Event('disclosure.show'));

        if (this.dataToggleExpanded) {
            this.trigger.textContent = this.dataToggleExpanded;
        }

        const shown = () => {
            self.trigger.dispatchEvent(new Event('disclosure.shown'));
            self.trigger.classList.remove('showing');
        }

        if (self.panel && self.slide) {
            new slideDown(self.panel, self.slideSpeed);
            setTimeout(() => {
                shown();
            }, self.slideSpeed);
        } else if (self.panel) {
            self.panel.style.display = this.displayProperty;
            // On rajoute un timeout insignifiant pour permettre à l'animation CSS d'être visible
            setTimeout(function () {
                self.panel.classList.add('is-visible');
            }, 10);
            setTimeout(function () {
                shown();
            }, this.delay + 20);
        }
        if (!this.disableAutoFocus) {
            self.panel.setAttribute('tabindex', '-1');
            self.panel.focus();
        }
        this.isExpanded = true;

        // On appelle la fonction outsideClickEvent quand le composant est visible
        if (this.outsideClick) {
            this.outsideClickEvent(this.trigger, this.panel);
        }
    }

    /**
     * Cache le contenu du panneau controllé
     */
    hide() {
        const self = this;
        const panel = this.panel;
        this.trigger.setAttribute("aria-expanded", "false");
        this.trigger.classList.add('hiding');
        this.trigger.dispatchEvent(new Event('disclosure.hide'));

        if (this.dataToggleCollapsed) {
            this.trigger.textContent = this.dataToggleCollapsed;
        }

        const hidden = () => {
            self.trigger.dispatchEvent(new Event('disclosure.hidden'));
            self.trigger.classList.remove('hiding');
        }

        const showInProgress = document.querySelector('.showing');
        // L'option disableAutoFocus empêche le retour de focus
        // Egalement si un autre événement show() est en cours, on désactive le focus car le retour de focus du show courant est prioritaire.
        if (!this.disableAutoFocus && !document.body.contains(showInProgress)) {
            this.trigger.focus();
        }
        if (panel && this.slide) {
            new slideUp(panel, this.slideSpeed);
            setTimeout(() => {
                hidden();
            }, this.slideSpeed);
        } else if (panel) {
            panel.classList.remove('is-visible');
            setTimeout(function () {
                panel.style.display = "none";
                hidden();
            }, this.delay);
        }
        this.isExpanded = false;
        // On enlève les écouteurs d'événements du panneau
        this.removePanelEventListener();
    }

    /**
     * Gestions des événements du clavier
     */
    onKeydown(event) {
        switch (event.key) {
            case 'Esc':
            case 'Escape':
                this.hide();
        }
    }

    /**
     * Gestion du clic en dehors du composant
     */
    outsideClickEvent(element, controlledElement) {
        const outsideClickEventListener = event => {
            if (!element.contains(event.target) && !controlledElement.contains(event.target)) {
                if (this.isVisible()) {
                    this.hide();
                }
                removeClickListener();
            }
        }

        const removeClickListener = () => {
            document.removeEventListener('click', outsideClickEventListener);
        }

        document.addEventListener('click', outsideClickEventListener);
    }

    /**
     * Affiche/Cache le contenu du nœud controllé en fonction de l'état précédent
     */
    toggleExpand() {
        if (this.trigger.getAttribute("aria-expanded") === "true") {
            this.hide();
        } else {
            this.show();
        }
    }

    /* EVENT HANDLERS - Bouton */
    onClick() {
        const triggerClassList = this.trigger.classList;
        // Si une animation est en cours, on annule le clic
        if (triggerClassList.contains('showing') || triggerClassList.contains('hiding')) return;
        this.toggleExpand();
    }

    onFocus() {
        this.trigger.classList.add("is-focus");
    }

    onBlur() {
        this.trigger.classList.remove("is-focus");
    }

    /* EVENT HANDLERS - Panneau */
    onTabKeyDown(e) {
        const currentTarget = e.currentTarget;
        // Écouter l'événement keydown pour vérifier si Tab ou Shift + Tab est pressé
        if (e.key === 'Tab') {
            // Donner au navigateur le temps de déplacer le focus avant de vérifier l'élément actif
            setTimeout(() => {
                const activeElement = document.activeElement;

                // Vérifie si le nouvel élément ayant le focus n'est pas un enfant de l'élément actuel
                if (currentTarget && !currentTarget.contains(activeElement)) {
                    if (this.isVisible()) {
                        if (this.closeOnBlur) {
                            this.hide();
                        }
                    }
                }

            }, this.slideSpeed + 10);
        }
    };

    throwErrors() {
        if (this.trigger.tagName !== 'BUTTON' || (this.trigger.hasAttribute("role") && this.trigger.getAttributeNode("role").value !== 'button')) {
            try {
                throw new Error(`Disclosure : L'initialisation doit se faire sur un élément <button> ou ayant un attribut ARIA [role="button"]`);
            } catch (error) {
                console.error(this.trigger, error);
            }
        }

        if (!this.trigger.hasAttribute('aria-controls')) {
            try {
                throw new Error(`Disclosure : L'attribut [aria-controls] est nécessaire sur l'élément <button> ou [role="button"]`);
            } catch (error) {
                console.error(this.trigger, error);
            }
        }

        if (!this.id) {
            try {
                throw new Error(`Disclosure : Vérifier que la valeur des attributs [aria-controls] et [id] soient identique`);
            } catch (error) {
                console.error(this.trigger, error);
            }
        }
    }

    /** la fonction destroy() remplace le nœud actuel par un clone. Ce qui a pour effet d'enlever les événements attachés au préalable.
     *   Pensez à refaire un document.querySelector pour récupérer le nouveau nœud créé. */
    destroy() {
        const current = this.trigger;
        const clone = current.cloneNode(true);
        current.parentNode.replaceChild(clone, current);
    }
}

/*
 *   @constructor CloseDisclosure
 */
export class DisclosureClose {
    constructor(trigger) {
        this.trigger = trigger;
        this.id = this.trigger.getAttribute("aria-controls");
    }

    init() {
        this.trigger.addEventListener("click", this.onClick.bind(this));
    }

    /* EVENT HANDLERS */

    onClick() {
        document.querySelector(`[aria-expanded="true"][aria-controls="${this.id}"]`)?.click();
    }

    destroy() {
        this.trigger.removeEventListener("click", this.onClick.bind(this));
    }
}
